import { useScatterplotContext } from 'features/timeSeries/ScatterplotContext'
import React, { useCallback, useRef, useState } from 'react'
import styled from 'styled-components'

const ResizeBar = styled.div<{ $visible: boolean; $side: 'left' | 'right' }>`
  position: absolute;
  ${({ $side }) => ($side === 'left' ? 'left: 0;' : 'right: 0;')}
  top: 0;
  height: 100%;
  width: 6px;
  transform: translateX(${({ $side }) => ($side === 'left' ? '-50%' : '50%')});
  background-color: transparent;
  cursor: col-resize;
  flex-shrink: 0;
  user-select: none;
  z-index: 100;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition:
    opacity 0.2s ease,
    background-color 0.2s ease;

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 2px;
    height: 30px;
    background-color: ${({ theme }) => theme.forecast.secondaryText};
    border-radius: 1px;
    opacity: 0.5;
    transition: all 0.2s ease;
  }

  &:hover {
    background-color: ${({ theme }) => theme.forecast.primary}20;

    &::after {
      opacity: 1;
      background-color: ${({ theme }) => theme.forecast.primary};
    }
  }

  &:active {
    background-color: ${({ theme }) => theme.forecast.primary}30;

    &::after {
      opacity: 1;
      background-color: ${({ theme }) => theme.forecast.primary};
    }
  }
`

interface ResizeHandleComponentProps {
  onWidthChange: (width: number | null) => void
  containerRef: React.RefObject<HTMLDivElement | null>
  /**
   * Which edge of the panel the handle sits on. For a panel docked to the right
   * of the chart the inner (chart-facing) edge is the left one, so dragging left
   * widens it; for a left-docked panel it is the right edge. Defaults to 'right'.
   */
  side?: 'left' | 'right'
}

export const ResizeHandleComponent: React.FC<ResizeHandleComponentProps> = ({
  onWidthChange,
  containerRef,
  side = 'right',
}) => {
  const { isResizing, setIsResizing } = useScatterplotContext()
  const [hasMoved, setHasMoved] = useState(false)
  const [visible, setVisible] = useState(false)
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const startXRef = useRef<number>(0)
  const startWidthRef = useRef<number>(0)
  const lastSnapRef = useRef<'default' | 'medium' | null>(null)

  const handleResizeStart = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      setHasMoved(false)
      startXRef.current = e.clientX
      if (containerRef.current) {
        startWidthRef.current = containerRef.current.offsetWidth
      }
      setIsResizing(true)
    },
    [containerRef, setIsResizing]
  )

  const handleResizeMove = useCallback(
    (e: MouseEvent) => {
      if (!isResizing) return

      // Set hasMoved flag if mouse has moved more than a few pixels
      if (!hasMoved && Math.abs(e.clientX - startXRef.current) > 3) {
        setHasMoved(true)
        lastSnapRef.current = null
      }

      const deltaX = e.clientX - startXRef.current
      // The handle sits on the panel's inner edge. For a right-docked panel that
      // edge is on the left, so dragging left (negative deltaX) must grow it.
      const newWidth =
        side === 'left'
          ? startWidthRef.current - deltaX
          : startWidthRef.current + deltaX
      const minWidth = 256 // 16rem in pixels

      if (newWidth >= minWidth) {
        onWidthChange(newWidth)
      }
    },
    [isResizing, hasMoved, onWidthChange, side]
  )

  const snapTo = useCallback(
    (target: 'default' | 'medium', mediumWidth: number) => {
      if (target === 'default') {
        onWidthChange(null)
      } else {
        onWidthChange(mediumWidth)
      }
      lastSnapRef.current = target
    },
    [onWidthChange]
  )

  const handleResizeEnd = useCallback(() => {
    if (isResizing && !hasMoved) {
      const mediumWidth = 448 // 28rem in pixels

      if (containerRef.current?.parentElement) {
        if (lastSnapRef.current) {
          // At a breakpoint — toggle to the other
          snapTo(
            lastSnapRef.current === 'default' ? 'medium' : 'default',
            mediumWidth
          )
        } else {
          // Manually resized — snap to closest
          const currentWidth = containerRef.current.offsetWidth
          const defaultWidth =
            containerRef.current.parentElement.offsetWidth * 0.5
          const closerToDefault =
            Math.abs(currentWidth - defaultWidth) <
            Math.abs(currentWidth - mediumWidth)
          snapTo(closerToDefault ? 'default' : 'medium', mediumWidth)
        }
      }
    }

    setHasMoved(false)
    setIsResizing(false)
  }, [isResizing, hasMoved, containerRef, snapTo, setIsResizing])

  React.useEffect(() => {
    return () => {
      if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current)
    }
  }, [])

  React.useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleResizeMove)
      document.addEventListener('mouseup', handleResizeEnd)
      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'
    } else {
      document.removeEventListener('mousemove', handleResizeMove)
      document.removeEventListener('mouseup', handleResizeEnd)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }

    return () => {
      document.removeEventListener('mousemove', handleResizeMove)
      document.removeEventListener('mouseup', handleResizeEnd)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
  }, [isResizing, handleResizeMove, handleResizeEnd])

  const handleMouseEnter = useCallback(() => {
    hoverTimerRef.current = setTimeout(() => setVisible(true), 200)
  }, [])

  const handleMouseLeave = useCallback(() => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current)
      hoverTimerRef.current = null
    }
    if (!isResizing) setVisible(false)
  }, [isResizing])

  return (
    <ResizeBar
      $visible={visible || isResizing}
      $side={side}
      onMouseDown={handleResizeStart}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    />
  )
}
